import React, { useContext, useState, useEffect } from 'react';
import { navigate, useStaticQuery, graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import { getShopifyImage } from 'gatsby-source-shopify';
import { useHits, Index } from 'react-instantsearch';
import styled, { css } from 'styled-components';
import {
  brandColours,
  fontSize,
  fontWeights,
  maxBreakpointQuery,
  minBreakpointQuery,
  standardColours,
  zIndexLayers,
} from '../styles';
import { Container, Link } from './ui';
import { buildUrl, slugify } from '../utils';
import { StoreContext } from '../context/StoreContext';
import { ProductPrice } from './Prices';
import ProductListing from './ProductListing';

const StyledSearchResult = styled.div`
  display: ${({ show }) => (show ? `block` : `none`)};
  width: 100%;
  background-color: ${standardColours.white};

  ${({ isSearchPage }) => {
    if (!isSearchPage) {
      return css`
        top: 100%;
        left: 0;
        z-index: ${zIndexLayers.fourth};

        ${maxBreakpointQuery.large`
          position: fixed;
          top: 108px;
          right: 0;
          bottom: 0;
          overflow: auto;
        `}

        ${minBreakpointQuery.large`
          position: absolute;
          border-top: 1px solid ${standardColours.lighterGrey};
        `}
      `;
    } else {
      return css`
        position: relative;

        ul {
          display: grid;

          ${minBreakpointQuery.tsmall`
            grid-template-columns: repeat(2, 1fr);
          `}

          ${minBreakpointQuery.smedium`
            grid-template-columns: repeat(3, 1fr);
          `}

          ${minBreakpointQuery.mlarge`
            grid-template-columns: repeat(4, 1fr);
          `}

          li {
            border-right: 1px solid ${standardColours.lighterGrey};
            border-bottom: 1px solid ${standardColours.lighterGrey};
          }
        }
      `;
    }
  }}
`;

const StyledProductResults = styled.div`
  padding-top: 12px;
  padding-bottom: 16px;

  ${minBreakpointQuery.tsmall`
    padding-top: 15px;
    padding-bottom: 22px;
    padding-left: 10px;
    padding-right: 10px;
  `}

  ${minBreakpointQuery.small`
    padding-top: 20px;
    padding-bottom: 30px;
    padding-left: 15px;
    padding-right: 15px;
  `}
`;

const StyledContainer = styled(Container)`
  padding-left: 12px;
  padding-right: 12px;
`;

const StyledInner = styled.div`
  ${({ notFound }) => {
    if (!notFound) {
      return css`
        ${minBreakpointQuery.tsmall`
          display: grid;
          grid-template-columns: 1.1fr 2fr;
          column-gap: 20px;
        `}

        ${minBreakpointQuery.smedium`
          grid-template-columns: 1fr 2.5fr;
        `}

        ${minBreakpointQuery.mlarge`
          grid-template-columns: 1.4fr 2.5fr;
          column-gap: 30px;
        `}

        ${minBreakpointQuery.large`
          grid-template-columns: 1.1fr 2fr;
          column-gap: 20px;
        `}

        ${minBreakpointQuery.large`
          grid-template-columns: 1fr 2.5fr;
        `}

        ${minBreakpointQuery.xxxlarge`
          grid-template-columns: 1.4fr 2.5fr;
        `}
      `;
    }
  }}
`;

const StyledSidebar = styled.div`
  ${minBreakpointQuery.tsmall`
    display: flex;
    flex-direction: column;
    row-gap: 12px;
    grid-row: 1;
  `}
`;

const StyledNotFound = styled.div`
  margin: 50px 10px;
  ${fontSize(14)};
  font-weight: ${fontWeights.semibold};
  text-align: center;
`;

const StyledNotFoundHeading = styled.p``;

const StyledNotFoundSubheading = styled.p`
  margin-top: 12px;
  color: ${standardColours.grey};
`;

const StyledProducts = styled.div``;

const StyledProductIntro = styled.div`
  display: flex;
  justify-content: space-between;
  column-gap: 5px;
`;

const StyledHeading = styled.h3`
  ${fontSize(14)}
  color: ${brandColours.primary};
`;

const StyledProductListing = styled.div`
  margin-top: 12px;

  ul {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;

    ${minBreakpointQuery.smedium`
      grid-template-columns: repeat(3, 1fr);
    `}

    ${minBreakpointQuery.mlarge`
      column-gap: 20px;
    `}

    ${minBreakpointQuery.large`
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
    `}

    ${minBreakpointQuery.xlarge`
      grid-template-columns: repeat(3, 1fr);
    `}

    li {
      border: 1px solid ${standardColours.lightGrey};
    }
  }
`;

const StyledSearchList = styled.div`
  ${maxBreakpointQuery.tsmall`
    margin-top: 12px;
  `}
`;

const StyledSearchListItem = styled.li`
  margin: 8px 0;
  ${fontSize(14)}
  text-transform: capitalize;
`;

const StyledItem = styled.li`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 12px 14px;
  cursor: pointer;
  ${fontSize(14)}

  ${minBreakpointQuery.tsmall`
    padding: 16px 20px;
  `}
`;

const StyledImage = styled(GatsbyImage)`
  margin: 0 auto;
`;

const StyledContent = styled.div`
  margin-top: 8px;

  ${minBreakpointQuery.tsmall`
    margin-top: 10px;
  `}
`;

const StyledName = styled.p``;

const StyledPrice = styled(ProductPrice)`
  margin-top: 6px;
  color: ${brandColours.primary};
`;

const PageHits = props => {
  const { setShowProductResults } = props;

  const { hits } = useHits(props);

  const { setSearchOpen } = useContext(StoreContext);

  useEffect(() => {
    setShowProductResults(hits.length > 0);
  }, [hits]);

  return (
    hits.length > 0 && (
      <StyledProducts>
        <StyledProductIntro>
          <StyledHeading>Products</StyledHeading>
        </StyledProductIntro>
        <StyledProductListing>
          <ul>
            {hits.map(hit => {
              const {
                title,
                handle,
                image,
                priceRangeV2,
                variants,
                m2Pricing,
                tags,
              } = hit;

              if (tags.find(tag => tag === 'algolia-ignore')) {
                return;
              }

              // Link directly to product handle
              hit.collections = [];

              const imageData =
                image &&
                getShopifyImage({
                  image: { originalSrc: image.src },
                  layout: 'fixed',
                  width: 65,
                  height: 65,
                });

              return (
                <StyledItem
                  onClick={() => {
                    setSearchOpen(false);
                    navigate(buildUrl(handle, hit));
                  }}
                >
                  {image && (
                    <StyledImage
                      image={imageData}
                      alt={image.altText || title}
                    />
                  )}
                  <StyledContent>
                    <StyledName>
                      <Link to={hit}>{title}</Link>
                    </StyledName>
                    <StyledPrice
                      priceRange={priceRangeV2}
                      variants={variants}
                      m2Pricing={m2Pricing && m2Pricing.value}
                      isSearchItem={true}
                    />
                  </StyledContent>
                </StyledItem>
              );
            })}
          </ul>
        </StyledProductListing>
      </StyledProducts>
    )
  );
};

const ListHits = props => {
  const { heading } = props;

  const { hits } = useHits(props);

  const { setSearchOpen } = useContext(StoreContext);

  if (heading === 'Categories') {
    props.setShowCategoryResults(hits.length > 0);
  } else if (heading === 'Advice & Guides') {
    props.setShowAdviceResults(hits.length > 0);
  } else if (heading === 'Suggested Searches') {
    props.setShowSuggestionResults(hits.length > 0);
  } else if (heading === 'Artwork Templates (Click to download)') {
    props.setShowArtworkTemplateResults(hits.length > 0);
  }

  return (
    hits.length > 0 && (
      <StyledSearchList>
        <StyledHeading>{heading}</StyledHeading>
        <ul>
          {props.items
            ? props.items.slice(0, 3).map(item => (
                <StyledSearchListItem>
                  <a
                    href={`/search/?term=${item}`}
                    onClick={() => {
                      setSearchOpen(false);
                    }}
                  >
                    {item}
                  </a>
                </StyledSearchListItem>
              ))
            : hits
                .slice(0, heading === 'Suggested Searches' ? 5 : 3)
                .map(hit => {
                  const { title, handle, slug, url, query } = hit;
                  return (
                    <StyledSearchListItem>
                      {handle || slug || url ? (
                        <Link
                          to={handle ? handle : slug ? slug : url}
                          onClick={() => {
                            setSearchOpen(false);
                          }}
                        >
                          {title}
                        </Link>
                      ) : (
                        <a
                          href={`/search/?term=${query}`}
                          onClick={() => {
                            setSearchOpen(false);
                          }}
                        >
                          {query}
                        </a>
                      )}
                    </StyledSearchListItem>
                  );
                })}
        </ul>
      </StyledSearchList>
    )
  );
};

const SearchResult = ({ results, query, show, isSearchPage }) => {
  const {
    datoCmsSite: { name },
  } = useStaticQuery(graphql`
    query SearchResultQuery {
      datoCmsSite {
        name
      }
    }
  `);

  const [showProductResults, setShowProductResults] = useState(false);
  const [showSuggestionResults, setShowSuggestionResults] = useState(false);
  const [showCategoryResults, setShowCategoryResults] = useState(true);
  const [showArtworkTemplateResults, setShowArtworkTemplateResults] =
    useState(false);
  const [showAdviceResults, setShowAdviceResults] = useState(true);

  const recentSearchLocalStorageKey = `${slugify(name)}:recent-searches`;

  const currentRecentSearches =
    JSON.parse(localStorage.getItem(recentSearchLocalStorageKey)) || [];

  return (
    <StyledSearchResult show={show} isSearchPage={isSearchPage}>
      {results ? (
        <ProductListing
          items={results.filter(
            result => !result.tags.includes('algolia-ignore'),
          )}
          isSearch={true}
        />
      ) : (
        <StyledProductResults>
          <StyledContainer>
            <StyledInner
              notFound={
                !showCategoryResults &&
                !showAdviceResults &&
                !showProductResults &&
                !showSuggestionResults &&
                !showArtworkTemplateResults
              }
            >
              <Index indexName="Products">
                <PageHits setShowProductResults={setShowProductResults} />
              </Index>
              <StyledSidebar>
                {!query && currentRecentSearches.length > 0 && (
                  <ListHits
                    heading="Recent Searches"
                    items={currentRecentSearches.reverse()}
                  />
                )}
                <Index indexName="Products_query_suggestions">
                  <ListHits
                    heading="Suggested Searches"
                    setShowSuggestionResults={setShowSuggestionResults}
                  />
                </Index>
                <Index indexName="shopify_collections">
                  <ListHits
                    heading="Categories"
                    setShowCategoryResults={setShowCategoryResults}
                  />
                </Index>
                <Index indexName="artwork_templates">
                  <ListHits
                    heading="Artwork Templates (Click to download)"
                    setShowArtworkTemplateResults={
                      setShowArtworkTemplateResults
                    }
                  />
                </Index>
                <Index indexName="advice_guides">
                  <ListHits
                    heading="Advice & Guides"
                    setShowAdviceResults={setShowAdviceResults}
                  />
                </Index>
                {!showCategoryResults &&
                  !showAdviceResults &&
                  !showProductResults &&
                  !showSuggestionResults &&
                  !showArtworkTemplateResults && (
                    <StyledNotFound>
                      <StyledNotFoundHeading>
                        Did you find what you are looking for?
                      </StyledNotFoundHeading>
                      <StyledNotFoundSubheading>
                        If not, try and rephrase your search.
                      </StyledNotFoundSubheading>
                    </StyledNotFound>
                  )}
              </StyledSidebar>
            </StyledInner>
          </StyledContainer>
        </StyledProductResults>
      )}
    </StyledSearchResult>
  );
};

export default SearchResult;
